/**
 * useCopyrights — 版权登记 hook(M4)
 *
 * 提供「我的版权列表」「申请登记」「下载 PDF」三类能力。
 * 公开验真请直接调 copyrightsApi.verify。
 */
import { useCallback, useEffect, useState } from 'react'

import {
  ApiError,
  copyrightsApi,
  type SilkCopyright,
  type SilkRegisterCopyrightBody,
} from '@/api'

interface State {
  items: SilkCopyright[]
  total: number
  loading: boolean
  error: string | null
}

const INIT: State = { items: [], total: 0, loading: false, error: null }

export function useMyCopyrights(opts: { enabled?: boolean; limit?: number; offset?: number } = {}) {
  const { enabled = true, limit = 20, offset = 0 } = opts
  const [state, setState] = useState<State>(INIT)

  const refresh = useCallback(async () => {
    if (!enabled) return
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const list = await copyrightsApi.list({ limit, offset })
      setState({ items: list.items, total: list.total, loading: false, error: null })
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message
      setState((s) => ({ ...s, loading: false, error: msg }))
    }
  }, [enabled, limit, offset])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const register = useCallback(
    async (body: SilkRegisterCopyrightBody) => {
      const created = await copyrightsApi.register(body)
      await refresh()
      return created
    },
    [refresh],
  )

  const downloadCertificate = useCallback(async (id: number) => {
    const blob = await copyrightsApi.downloadCertificate(id)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `copyright-${id}.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }, [])

  return { ...state, refresh, register, downloadCertificate }
}
